import { memo, useCallback, useMemo } from 'react';
import { type TestProps } from '@direct-frontend/components/testing';
import { isString } from '@direct-frontend/stdlib';
import { TagTreeField, type TagTreeFieldProps } from '#shared/components/TagTreeField';
import { createMapFromArray } from '#shared/lib/array';
import { useCategoryGetAll } from '../../api';
import { getCategoryChain } from '../../lib/getCategoryChain';

type SelectCategory = {
    id: string;
    parent_id?: string;
    title: string;
};

type SelectCategoriesFieldAsyncProps = {
    categories: SelectCategory[] | undefined | null;
    isFetching: boolean;
    isUninitialized: boolean;
    isError: boolean;
};

export interface SelectCategoriesFieldProps
    extends TestProps,
        Omit<TagTreeFieldProps, 'onChange' | 'items' | 'tagEditorFactory'> {
    value: string[];
    onChange: (value: string[]) => void;
}

export const SelectCategoriesField = memo<SelectCategoriesFieldProps>(function SelectCategoriesField(props) {
    const { data: categories, isFetching, isUninitialized, isError } = useCategoryGetAll({});

    return (
        <SelectCategoriesFieldGroup
            {...props}
            categories={categories}
            isFetching={isFetching}
            isUninitialized={isUninitialized}
            isError={isError}
        />
    );
});

export const SelectCategoriesFieldGroup = memo<SelectCategoriesFieldProps & SelectCategoriesFieldAsyncProps>(
    function SelectCategoriesField({
        'data-testid': dataTestId = 'SelectCategoriesField',
        value,
        onChange,
        isDisabled,
        isLoading,
        validationState,
        placeholder,
        popupPlacement,
        categories,
        isFetching,
        isUninitialized,
        isError,
        ...props
    }) {
        const mapCategories = useMemo(
            () => (categories ? createMapFromArray(categories, (item) => [item.id, item]) : new Map()),
            [categories]
        );

        const items = useMemo(
            () =>
                (categories ?? []).map((category) => {
                    return {
                        id: category.id,
                        parentId: category.parent_id,
                        title: category.title,
                    };
                }),
            [categories]
        );

        const getTagLabel = useCallback(
            (id: string) =>
                getCategoryChain(id, { mapCategories })
                    .map((category) => {
                        if (isString(category)) {
                            return category;
                        }

                        return category.title;
                    })
                    .join(' → '),
            [mapCategories]
        );

        return (
            <TagTreeField
                {...props}
                value={value}
                onChange={onChange}
                items={items}
                isLoading={isLoading || isFetching || isUninitialized}
                isDisabled={isDisabled || isFetching || isUninitialized || isError}
                validationState={isError ? 'invalid' : validationState}
                placeholder={placeholder ?? 'Выберите категорию'}
                emptySectionText="Категорий по запросу не найдено"
                data-testid={dataTestId}
                getTagLabel={getTagLabel}
            />
        );
    }
);
